import type { MobiLauncherConfig } from '@schema/index.js';

/**
 * Per-tenant deployment pack — what a fleet admin hands to their MDM
 * console to wire MobiLauncher into a kiosk-capable lockdown profile.
 *
 * The deliverable is intentionally MDM-agnostic. Every kiosk-capable
 * MDM we've seen (SOTI MobiControl, Intune, Workspace ONE, Scalefusion,
 * Hexnode, Esper) uses the same conceptual primitives — a kiosk
 * URL, an allowed-apps list, and a few status-bar / navigation
 * toggles — so the same JSON drives setup across all of them. The
 * markdown guide walks each MDM's specific UI path.
 *
 * Format is JSON because (a) any MDM-importer tool can parse it,
 * (b) it round-trips trivially, and (c) it doesn't pretend to be a
 * SOTI Lockdown XML we haven't validated against a real environment.
 * Once we have MobiControl access, a vendor-specific XML exporter
 * is a small follow-up that reads this same JSON.
 */
export interface DeploymentPack {
  schemaVersion: '1.0';
  /** Stable identifier of the tenant config. */
  tenantId: string;
  /** Human-readable deployment name (matches the config). */
  deploymentName: string;
  /** URL devices point at — `https://app.mobilauncher.com/t/<tenantId>`. */
  launcherUrl: string;
  /** Which MDM the tenant declared at signup (`none` = browser-kiosk). */
  mdm: string;
  /** All package names referenced by App tiles + a small bundle of system
   *  essentials (Settings, Files) so kiosk admins can fix devices. */
  allowedPackages: string[];
  /** App-tile labels paired with their packages, for the MDM's
   *  allow-list UI which usually expects human-readable names. */
  apps: Array<{ label: string; packageName: string }>;
  /** Optional Pro-tier offline-bundle URL — present when the tenant is
   *  Pro/Enterprise. Devices behind a captive portal can install once. */
  offlineBundleUrl?: string;
}

/** System packages we always include so a locked kiosk isn't a brick. */
const SYSTEM_ESSENTIALS: readonly string[] = [
  'com.android.settings',
  'com.android.systemui',
  'com.google.android.documentsui',
];

/**
 * Build the pack from a parsed config. Pulls App-tile packages out of
 * every profile's grid + tools + featured tiles + folder children, so
 * a multi-profile config doesn't accidentally allow only one profile's
 * apps. De-duplicates and sorts for stable file output (diffable across
 * builds).
 */
export function buildDeploymentPack(input: {
  config: MobiLauncherConfig;
  launcherUrl: string;
}): DeploymentPack {
  const { config, launcherUrl } = input;
  const apps = collectApps(config);
  const allowedPackages = Array.from(
    new Set([...apps.map((a) => a.packageName), ...SYSTEM_ESSENTIALS]),
  ).sort();

  return {
    schemaVersion: '1.0',
    tenantId: config.tenantId,
    deploymentName: config.deploymentName,
    launcherUrl,
    mdm: config.mdm,
    allowedPackages,
    apps,
  };
}

function collectApps(
  config: MobiLauncherConfig,
): Array<{ label: string; packageName: string }> {
  const out = new Map<string, string>(); // packageName → label
  for (const profile of config.profiles) {
    walkTiles(profile.grid.tiles, out);
    walkTiles(profile.featuredTiles, out);
    walkTiles(profile.tools, out);
  }
  return [...out.entries()]
    .map(([packageName, label]) => ({ label, packageName }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function walkTiles(
  tiles: readonly { type: string; [k: string]: unknown }[],
  out: Map<string, string>,
): void {
  for (const t of tiles) {
    if (t.type === 'app' && typeof t['packageName'] === 'string') {
      const label = typeof t['label'] === 'string' ? t['label'] : t['packageName'];
      // First-seen-label-wins for dupe packages — matches MDM UI
      // expectations where each package is listed once.
      if (!out.has(t['packageName'])) out.set(t['packageName'], label);
    }
    if (t.type === 'folder' && Array.isArray(t['children'])) {
      walkTiles(t['children'] as { type: string; [k: string]: unknown }[], out);
    }
  }
}

/**
 * Render the per-MDM setup guide as Markdown. Same content for every
 * tenant aside from the launcher URL + app list, which are filled in
 * from the pack so the admin doesn't have to re-derive them.
 */
export function buildDeploymentGuide(pack: DeploymentPack): string {
  return `# MobiLauncher deployment guide

## ${pack.deploymentName}

Tenant: \`${pack.tenantId}\`
Launcher URL: \`${pack.launcherUrl}\`

Hand this file to whoever owns your MDM console. Every kiosk-capable
MDM has the same three primitives we need to set — they just call
them different things. The MDM-specific sections below explain where
to click.

---

## 1. Allowed apps

Add each of these packages to your MDM's allow-list / kiosk app list:

${pack.apps.map((a) => `- **${a.label}** — \`${a.packageName}\``).join('\n')}

System packages (don't lock these out or the device can't recover):

${SYSTEM_ESSENTIALS.map((p) => `- \`${p}\``).join('\n')}

## 2. Kiosk URL

Set the kiosk-mode browser to load:

\`\`\`
${pack.launcherUrl}
\`\`\`

The launcher takes over from there — every tile launches one of the
allowed apps via Android intent.

## 3. Per-MDM specifics

### SOTI MobiControl

1. Configure → Profiles → Add → Android → Lockdown
2. Lockdown Type: **Speed Lockdown**
3. Default app: **Chrome (or your preferred managed browser)**
4. Default URL: paste the launcher URL above
5. Allowed Apps: paste the package list above
6. Hide status bar: optional, depends on whether you want the OS clock
7. Disable Recents, Home, Back keys: yes for kiosk; no for back-office
8. Assign the profile to your device group

### Microsoft Intune

1. Devices → Configuration → Create → Android Enterprise → Device
   restrictions
2. Kiosk → Multi-app kiosk (or Single-app for one launcher tile only)
3. Apps and folders: add the package list above
4. Sign-in app: launcher URL (use Managed Home Screen → Web Apps if
   you don't have a native kiosk-browser package)

### Scalefusion

1. Profiles → Android → New → Kiosk
2. Multi-app Kiosk → Browser Kiosk
3. Default URL: paste the launcher URL
4. Allowed apps: the package list above
5. Status bar / Hardware keys: per your kiosk requirements

### Hexnode / Esper / Workspace ONE

Same shape — every MDM with a kiosk-capable browser payload accepts
a launcher URL + an allowed-app list. The MDM-specific docs walk
the exact path.

---

## Verifying

Boot a kiosk that's enrolled in the profile. You should see:
1. MobiLauncher loads the configured tile grid
2. Tapping a tile launches the matching allowed app
3. The status bar respects your configuration
4. Back / Home / Recents are gated as configured

If a tile flashes a "this would launch X" toast instead of opening,
the package isn't in the allow-list — re-check step 1 above.
`;
}
